import React, { useState, useEffect } from 'react'
import { X, Sparkles, Plus, Trash2, ChevronRight, Loader2, Wand2, CheckCircle2, Download } from 'lucide-react'
import FileDropzone from './FileDropzone'
import { aiOutline, aiGenerate, fetchVideoCatalog } from '../api/lessonBuilderApi'
import { normalizeBlocks, lessonCtx } from '../types/blocks'
import type { LessonOutline, OutlineSection } from '../api/lessonBuilderApi'
import { useLessonBuilder } from '../store/useLessonBuilder'
import { genId } from '../lib/id'
import { GeoGebraService } from '../../../services/video/GeoGebraService'
import { LabService } from '../../../services/lab/LabService'
import { englishContentService } from '../../../services/english/englishContentService'
import type { RichCard, SimulationCard, LabCard, EnglishCard, VideoCard, QuizCard, GameCard, LiveSessionCard } from '../types/LessonDoc'

const DOCX_ACCEPT = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
}

// Tên 4 hoạt động theo Công văn 5512 (kind 'ket_thuc' cũ được normalize → 'van_dung' khi nạp khung)
const KIND_LABEL: Record<string, string> = {
  mo_dau: 'Khởi động',
  chinh: 'Hình thành kiến thức',
  luyen_tap: 'Luyện tập',
  van_dung: 'Vận dụng',
}

// Quy đổi kind cũ/lạ về 1 trong 4 hoạt động chuẩn (tránh trùng mục trong dropdown).
const normalizeOutlineKinds = (o: LessonOutline): LessonOutline => ({
  ...o,
  sections: (o.sections || []).map((s: any) => ({
    ...s,
    kind: s.kind === 'ket_thuc' ? 'van_dung' : (KIND_LABEL[s.kind] ? s.kind : 'chinh'),
  })),
})
const kindRe = (k: string) =>
  k === 'mo_dau' ? /khởi động|mở đầu/i
  : k === 'luyen_tap' ? /luyện tập|củng cố/i
  : k === 'van_dung' || k === 'ket_thuc' ? /vận dụng|kết thúc|infographic/i
  : /hình thành|kiến thức|nội dung|chính/i

interface Props {
  isOpen: boolean
  onClose: () => void
  initialFile?: File | null   // file Word kéo-thả từ ngoài → tự chạy bước "đề xuất khung"
}

type Step = 'upload' | 'outline' | 'review'

// Thông điệp xoay vòng khi chờ AI — người dùng biết hệ thống đang chạy, không bị treo
const AI_MESSAGES: Record<'outline' | 'generate', string[]> = {
  outline: [
    'Đang đọc tài liệu Word…',
    'Trích công thức & tách hình ảnh…',
    'Nhận diện các đơn vị kiến thức…',
    'Phác khung bài giảng theo từng bước…',
  ],
  generate: [
    'Đang giải mã yêu cầu cần đạt…',
    'Trực quan hoá kiến thức bằng hình ảnh & mô phỏng…',
    'Chia nhỏ thành từng bước học dễ tiếp thu…',
    'Soạn câu hỏi tương tác chốt từng ý…',
    'Cài bẫy lỗi hiểu sai vào phương án nhiễu…',
    'Tinh chỉnh theo tinh thần đề 2026…',
    'Hoàn thiện trải nghiệm học VISE…',
  ],
}

const AiLessonWizard: React.FC<Props> = ({ isOpen, onClose, initialFile }) => {
  const { lesson, addPreparedCard, setMeta } = useLessonBuilder()
  const [step, setStep] = useState<Step>('upload')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sourceId, setSourceId] = useState('')
  const [outline, setOutline] = useState<LessonOutline | null>(null)
  const [generated, setGenerated] = useState<{ title: string; kind: string; items: any[] }[] | null>(null)
  const [aiMsgIdx, setAiMsgIdx] = useState(0)
  const [fakeProgress, setFakeProgress] = useState(0)

  // Pha AI đang chạy: bước upload = phân tích khung; bước outline = sinh chi tiết
  const busyPhase: 'outline' | 'generate' = step === 'upload' ? 'outline' : 'generate'

  // Khi chờ AI: xoay thông điệp + tiến độ giả lập tiệm cận (1 call duy nhất, không có progress thật)
  useEffect(() => {
    if (!busy) return
    setAiMsgIdx(0)
    setFakeProgress(4)
    const tMsg = setInterval(() => setAiMsgIdx((i) => i + 1), 2600)
    const tBar = setInterval(() => setFakeProgress((p) => p + (92 - p) * 0.06), 700)
    return () => { clearInterval(tMsg); clearInterval(tBar) }
  }, [busy])

  const reset = () => {
    setStep('upload'); setBusy(false); setError(null)
    setSourceId(''); setOutline(null); setGenerated(null)
  }
  const close = () => { reset(); onClose() }

  // ── B1: phân tích Word → khung ──
  const handleFile = async (file: File) => {
    setBusy(true); setError(null)
    try {
      const res = await aiOutline(file)
      if (!res?.outline?.sections?.length) throw new Error('AI không trả về khung hợp lệ.')
      setSourceId(res.source_id)
      setOutline(normalizeOutlineKinds(res.outline))
      setStep('outline')
    } catch (e) {
      setError((e as Error)?.message || 'Không phân tích được file Word')
    } finally { setBusy(false) }
  }

  // ── Sửa khung ──
  const patchOutline = (fn: (o: LessonOutline) => LessonOutline) =>
    setOutline((o) => (o ? fn(JSON.parse(JSON.stringify(o))) : o))
  const setSecField = (si: number, patch: Partial<OutlineSection>) =>
    patchOutline((o) => { o.sections[si] = { ...o.sections[si], ...patch }; return o })
  const addTopic = (si: number) =>
    patchOutline((o) => { o.sections[si].topics.push({ heading: '', hint: '' }); return o })
  const removeTopic = (si: number, ti: number) =>
    patchOutline((o) => { o.sections[si].topics.splice(ti, 1); return o })
  const setTopic = (si: number, ti: number, patch: Partial<{ heading: string; hint: string }>) =>
    patchOutline((o) => { o.sections[si].topics[ti] = { ...o.sections[si].topics[ti], ...patch }; return o })
  const addSection = () =>
    patchOutline((o) => { o.sections.push({ title: 'Phần mới', kind: 'chinh', topics: [{ heading: '', hint: '' }] }); return o })
  const removeSection = (si: number) =>
    patchOutline((o) => { o.sections.splice(si, 1); return o })

  // ── B2: sinh chi tiết ──
  const handleGenerate = async () => {
    if (!outline || !sourceId) return
    setBusy(true); setError(null)
    try {
      const res = await aiGenerate(sourceId, outline, lessonCtx(lesson.meta))
      if (!res?.sections?.length) throw new Error('AI không trả về nội dung.')
      setGenerated(res.sections)
      setStep('review')
    } catch (e) {
      setError((e as Error)?.message || 'Không sinh được nội dung chi tiết')
    } finally { setBusy(false) }
  }

  // ── Đưa vào bài giảng (cấu trúc Công văn 5512) ──
  const handleInsert = async () => {
    if (!generated) return
    const ctx = lessonCtx(lesson.meta)
    generated.forEach((sec) => {
      const items = normalizeBlocks(sec.items, ctx)   // chuẩn hoá về khối tương thích renderer + resolve simulation_ref
      if (!items.length) return
      const card: RichCard = { id: genId(), type: 'rich', items: items as any, title: sec.title }
      addPreparedCard(card, kindRe(sec.kind))
    })

    const KIEN_THUC_RE = /hình thành|kiến thức|nội dung|chính/i
    const LUYEN_TAP_RE = /luyện tập|củng cố/i
    const MO_RONG_RE = /mở rộng/i

    // Auto-gắn tài nguyên tương ứng theo Lớp/Chương/Bài để phong phú nội dung
    const lop = Number(lesson.meta.lop), chuong = Number(lesson.meta.chuong), bai = Number(lesson.meta.bai)
    if (lop && chuong && bai) {
      // Mô phỏng GeoGebra → Hình thành kiến thức
      GeoGebraService.getByLesson(lop, chuong, bai).forEach((g) => {
        const sim: SimulationCard = { id: genId(), type: 'simulation', kind: 'geogebra', ggbFile: g.filename, title: g.title, height: 520 }
        addPreparedCard(sim, KIEN_THUC_RE)
      })
      // Thí nghiệm Lab IoT → Hình thành kiến thức (GV kéo thả vào ĐÚNG Hoạt động liên quan)
      LabService.getByLesson(lop, chuong, bai).forEach((e) => {
        const lab: LabCard = { id: genId(), type: 'lab', url: LabService.fullUrl(e), expTitle: e.title, embed: false }
        addPreparedCard(lab, KIEN_THUC_RE)
      })
      // LUYỆN TẬP — bắt đầu bằng Video YouTube từ kho theo bài (nếu GV đã tạo)
      try {
        const videos = await fetchVideoCatalog(lop, chuong, bai)
        videos.slice(0, 2).forEach((v) => {
          const vc: VideoCard = { id: genId(), type: 'video', youtubeId: v.youtubeId, title: v.title }
          addPreparedCard(vc, LUYEN_TAP_RE)
        })
      } catch { /* chưa có kho video → bỏ qua */ }
      // Bài Tiếng Anh Vật lí → Mở rộng (ngoài các hoạt động chính)
      try {
        const eng = await englishContentService.getLesson(lop, chuong, bai)
        if (eng) {
          const en: EnglishCard = { id: genId(), type: 'english', lop, chuong, bai, engTitle: eng.title }
          addPreparedCard(en, MO_RONG_RE)
        }
      } catch { /* không có bài tiếng Anh tương ứng → bỏ qua */ }
      // Game Survival Runner → Mở rộng
      const game: GameCard = { id: genId(), type: 'game', game: 'survival', lop, chuong, bai, title: 'Game ôn tập: Survival Runner' }
      addPreparedCard(game, MO_RONG_RE)
    }

    // LUYỆN TẬP — sau video là Trắc nghiệm PMaker (GV chọn đề trong builder)
    const quiz: QuizCard = { id: genId(), type: 'quiz', source: 'pmaker', questions: [], title: 'Trắc nghiệm luyện tập (chọn đề PMaker)' }
    addPreparedCard(quiz, LUYEN_TAP_RE)

    // Phiên trực tiếp → 1 hoạt động trong Hình thành kiến thức; CHỈ 1 lần/bài (GV có thể xoá)
    const hasLive = lesson.sections.some((s) => s.cards.some((c) => c.type === 'live_session'))
    if (!hasLive) {
      const live: LiveSessionCard = { id: genId(), type: 'live_session', title: 'Phiên trực tiếp', note: 'Tổ chức hỏi đáp/thảo luận trực tiếp cho hoạt động này' }
      addPreparedCard(live, KIEN_THUC_RE)
    }

    if (!lesson.meta.tieuDe?.trim() && outline?.title) setMeta({ tieuDe: outline.title })
    close()
  }

  // Mở wizard kèm file kéo-thả từ ngoài → tự chạy bước phân tích → khung (không cần thả lại)
  useEffect(() => {
    if (isOpen && initialFile && step === 'upload' && !busy && !outline) {
      handleFile(initialFile)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialFile])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="relative bg-[#fdfcf0] rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* ── Overlay chờ AI — phòng thí nghiệm AI (che toàn modal khi đang gọi AI) ── */}
        {busy && (
          <div className="absolute inset-0 z-20 bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-900 flex flex-col items-center justify-center gap-6 px-10">
            {/* Hạt sáng nền */}
            <div className="absolute top-10 left-14 w-1.5 h-1.5 rounded-full bg-fuchsia-400/70 animate-ping" />
            <div className="absolute top-24 right-20 w-1 h-1 rounded-full bg-indigo-300/70 animate-ping" style={{ animationDelay: '0.8s' }} />
            <div className="absolute bottom-20 left-28 w-1 h-1 rounded-full bg-violet-300/60 animate-ping" style={{ animationDelay: '1.5s' }} />
            <div className="absolute bottom-12 right-28 w-1.5 h-1.5 rounded-full bg-purple-400/60 animate-ping" style={{ animationDelay: '2.2s' }} />

            {/* Quả cầu AI phát sáng */}
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-purple-500/30 blur-2xl animate-pulse" />
              <div className="absolute -inset-2 rounded-full border border-indigo-400/30 animate-spin" style={{ animationDuration: '6s' }}>
                <div className="absolute -top-1 left-1/2 w-2 h-2 rounded-full bg-fuchsia-400 shadow-[0_0_10px_rgba(232,121,249,0.9)]" />
              </div>
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-700 flex items-center justify-center shadow-[0_0_45px_rgba(167,139,250,0.55)]">
                <Sparkles className="w-9 h-9 text-white animate-pulse" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <p className="text-white font-bold text-base tracking-wide">
                {busyPhase === 'outline' ? 'AI đang phân tích tài liệu' : 'AI đang soạn nội dung chi tiết'}
              </p>
              {/* Huy hiệu phương pháp VISE — hiện rõ khi AI xử lí */}
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <span className="px-2.5 py-0.5 rounded-full bg-fuchsia-500/25 border border-fuchsia-400/50 text-fuchsia-100 text-xs font-black tracking-[0.2em] shadow-[0_0_14px_rgba(232,121,249,0.5)]">VISE</span>
                {['Trực quan', 'Chia nhỏ', 'Từng bước', 'Tương tác'].map((w) => (
                  <span key={w} className="px-2 py-0.5 rounded-full bg-white/10 border border-white/10 text-indigo-100 text-[11px] font-semibold">{w}</span>
                ))}
              </div>
              <p key={aiMsgIdx} className="text-indigo-200/90 text-sm min-h-[20px]">
                {AI_MESSAGES[busyPhase][aiMsgIdx % AI_MESSAGES[busyPhase].length]}
              </p>
            </div>

            {/* Thanh tiến độ (giả lập tiệm cận — báo hệ thống vẫn đang chạy) */}
            <div className="w-72">
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-indigo-400 via-violet-400 to-fuchsia-400 transition-all duration-700 rounded-full"
                  style={{ width: `${Math.min(95, fakeProgress)}%` }} />
              </div>
            </div>

            <p className="text-[11px] text-indigo-400/70">
              {busyPhase === 'outline' ? 'Khoảng 10–20 giây — đang chuyển công thức MathType' : 'Khoảng 30–60 giây — vui lòng không đóng cửa sổ'}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white/70">
          <h2 className="text-lg font-bold text-violet-700 flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Soạn bài giảng bằng AI
          </h2>
          <button onClick={close} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
        </div>

        {/* Steps indicator */}
        <div className="px-6 py-2 flex items-center gap-2 text-xs font-semibold border-b border-gray-100 bg-white/40">
          {(['upload', 'outline', 'review'] as Step[]).map((s, i) => (
            <React.Fragment key={s}>
              {i > 0 && <ChevronRight className="w-3 h-3 text-gray-300" />}
              <span className={step === s ? 'text-violet-700' : 'text-gray-400'}>
                {i + 1}. {s === 'upload' ? 'Tải Word' : s === 'outline' ? 'Khung nội dung' : 'Duyệt & đưa vào'}
              </span>
            </React.Fragment>
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1">
          {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

          {/* STEP 1 */}
          {step === 'upload' && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">Kéo-thả file Word (.docx). Hệ thống tách <b>khung bài giảng</b> để bạn duyệt trước khi số hoá chi tiết.</p>
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3 py-2.5 text-[13px] text-gray-700">
                <b className="text-amber-800">Soạn đúng form KHBD 5512</b> (I. Khởi động · II. Hình thành kiến thức · III. Luyện tập · IV. Vận dụng; phần II có thể nhiều "Hoạt động"). File đúng form sẽ được tách khung <b>tự động & chính xác (không tốn điểm)</b>; file tự do thì AI tự phác khung <span className="text-violet-600 font-semibold">(~2 điểm)</span>.
              </div>
              <FileDropzone
                accept={DOCX_ACCEPT}
                onFile={handleFile}
                busy={busy}
                label="Kéo-thả .docx → tách khung bài giảng"
                hint="pmaker chuyển MathType→công thức, tách ảnh (~10-20s)"
              />
              {/* Nút tải file Word mẫu — đặt NGAY DƯỚI khung kéo-thả, to & nổi bật */}
              <a href="/mau-khbd-5512.docx" download
                className="flex items-center justify-center gap-2.5 w-full px-5 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-base font-extrabold shadow-md shadow-amber-500/30 transition-all hover:shadow-lg hover:-translate-y-0.5">
                <Download className="w-5 h-5" /> Tải file Word giáo án mẫu (KHBD 5512)
              </a>
              <p className="text-center text-xs text-gray-400">Chưa có file? Tải mẫu trên về, điền nội dung rồi kéo-thả lên.</p>
            </div>
          )}

          {/* STEP 2: outline editor */}
          {step === 'outline' && outline && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tên bài giảng</label>
                <input value={outline.title} onChange={(e) => patchOutline((o) => { o.title = e.target.value; return o })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-violet-500/20" />
              </div>
              {outline.sections.map((sec, si) => (
                <div key={si} className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <input value={sec.title} onChange={(e) => setSecField(si, { title: e.target.value })}
                      className="flex-1 px-2 py-1.5 rounded-lg border border-gray-200 font-semibold text-gray-800 outline-none" />
                    <select value={sec.kind} onChange={(e) => setSecField(si, { kind: e.target.value as OutlineSection['kind'] })}
                      className="text-xs px-2 py-1.5 rounded-lg border border-gray-200 bg-white">
                      {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <button onClick={() => removeSection(si)} className="p-1.5 text-red-400 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <div className="space-y-1.5 pl-2">
                    {sec.topics.map((tp, ti) => (
                      <div key={ti} className="flex items-start gap-2">
                        <div className="flex-1 space-y-1">
                          <input value={tp.heading} onChange={(e) => setTopic(si, ti, { heading: e.target.value })}
                            placeholder="Tiêu đề ý" className="w-full px-2 py-1 rounded border border-gray-200 text-sm outline-none" />
                          <input value={tp.hint || ''} onChange={(e) => setTopic(si, ti, { hint: e.target.value })}
                            placeholder="Mô tả ngắn (AI sẽ bám theo)" className="w-full px-2 py-1 rounded border border-gray-100 text-xs text-gray-500 outline-none" />
                        </div>
                        <button onClick={() => removeTopic(si, ti)} className="p-1 text-gray-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                      </div>
                    ))}
                    <button onClick={() => addTopic(si)} className="text-xs text-violet-600 font-semibold inline-flex items-center gap-1 mt-1"><Plus className="w-3 h-3" /> Thêm ý</button>
                  </div>
                </div>
              ))}
              <button onClick={addSection} className="text-sm text-gray-600 font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-dashed border-gray-300 hover:bg-gray-50"><Plus className="w-4 h-4" /> Thêm phần</button>
            </div>
          )}

          {/* STEP 3: review */}
          {step === 'review' && generated && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-700 text-sm font-semibold"><CheckCircle2 className="w-5 h-5" /> AI đã sinh xong nội dung chi tiết.</div>
              {generated.map((sec, i) => (
                <div key={i} className="rounded-lg border border-gray-200 bg-white px-4 py-2.5 flex items-center justify-between">
                  <span className="font-medium text-gray-800">{sec.title}</span>
                  <span className="text-xs text-gray-400">{KIND_LABEL[sec.kind] || sec.kind} · {Array.isArray(sec.items) ? sec.items.length : 0} mục</span>
                </div>
              ))}
              <p className="text-xs text-gray-500">Bấm "Đưa vào bài giảng" để chèn vào đúng các phần. Sau đó bạn có thể tinh chỉnh từng thẻ bằng trình soạn thảo như bình thường.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-white/60 flex items-center justify-between">
          <button onClick={close} className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg">Đóng</button>
          <div className="flex items-center gap-2">
            {step === 'outline' && (
              <>
                <span className="text-[11px] text-gray-400">~10 điểm AI</span>
                <button onClick={handleGenerate} disabled={busy}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Sinh nội dung chi tiết
                </button>
              </>
            )}
            {step === 'review' && (
              <button onClick={handleInsert}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-bold">
                <CheckCircle2 className="w-4 h-4" /> Đưa vào bài giảng
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AiLessonWizard
